import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ShoppingBag, Package, Clock, CheckCircle, Truck, ArrowRight, Receipt } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PWAOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const clientToken = localStorage.getItem('clientToken');

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client/orders`, {
        headers: { Authorization: `Bearer ${clientToken}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [clientToken]);

  useEffect(() => {
    if (clientToken) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [clientToken, fetchOrders]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'processing': return <Package className="h-4 w-4 text-blue-500" />;
      case 'shipped': return <Truck className="h-4 w-4 text-purple-500" />;
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Receipt className="h-4 w-4 text-stone-500" />;
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'unpaid') {
      return <Badge className="bg-red-100 text-red-700">Unpaid</Badge>;
    }
    
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700">Pending</Badge>,
      processing: <Badge className="bg-blue-100 text-blue-700">Processing</Badge>,
      shipped: <Badge className="bg-purple-100 text-purple-700">Shipped</Badge>,
      completed: <Badge className="bg-green-100 text-green-700">Completed</Badge>,
    };
    return badges[status] || <Badge className="bg-stone-100 text-stone-700">{status}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (!clientToken) {
    return (
      <div className="px-4 pt-6 space-y-6">
        <div className="text-center space-y-4 py-12">
          <div className="bg-stone-100 p-4 rounded-full w-fit mx-auto">
            <ShoppingBag className="h-10 w-10 text-stone-400" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-stone-800">Sign in to view orders</h2>
            <p className="text-sm text-stone-500 mt-1">
              Create an account or sign in to track your orders
            </p>
          </div>
          <Link to="/app/account">
            <Button className="btn-action">
              Sign In
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading text-stone-800">My Orders</h1>
        <p className="text-stone-500 text-sm mt-1">Track your photo orders</p>
      </div>

      {/* Orders List */}
      {orders.length > 0 ? (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card key={order.id} className="border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(order.status)}
                    <span className="font-medium text-stone-800">
                      Order #{order.id.slice(-6).toUpperCase()}
                    </span>
                  </div>
                  {getStatusBadge(order.status, order.payment_status)}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-stone-600">
                    <span>Date</span>
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Items</span>
                    <span>{order.items?.length || 0} items</span>
                  </div>
                  <div className="flex justify-between font-medium text-stone-800 pt-2 border-t border-stone-100">
                    <span>Total</span>
                    <span className="text-amber-600">${order.total?.toFixed(2)}</span>
                  </div>
                </div>

                {order.order_type === 'pre-order' && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-stone-500">
                      Pre-order for: {order.event_name}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="bg-stone-100 p-4 rounded-full w-fit mx-auto mb-4">
            <ShoppingBag className="h-10 w-10 text-stone-400" />
          </div>
          <h2 className="text-lg font-medium text-stone-800">No orders yet</h2>
          <p className="text-sm text-stone-500 mt-1">
            Your orders will appear here after you place them
          </p>
          <Link to="/app">
            <Button className="mt-4 btn-action">
              Browse Galleries
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default PWAOrders;
